import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SearchableSelect } from '@/components/common/searchable-select';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  type OnFarmImplantPregCheckRow,
  OnFarmImplantRequestError,
  useRecordOnFarmImplantPregChecks,
} from '@/services/on-farm-implant';
import { toWallClockDate } from '@/utils';

import { RESULT_OPTIONS } from './columnDefs';
import { useCheckedByOptions } from './useCheckedByOptions';

type UpdateResultsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  rows: OnFarmImplantPregCheckRow[];
  onSaved: () => void;
};

export const UpdateResultsDialog = ({
  open,
  onOpenChange,
  sessionId,
  rows,
  onSaved,
}: UpdateResultsDialogProps) => {
  const { t } = useTranslation();
  const { techOptions, defaultCheckedById } = useCheckedByOptions();

  const [result, setResult] = useState('');
  const [checkDate, setCheckDate] = useState<Date | undefined>(
    () => new Date(),
  );
  const [checkedById, setCheckedById] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: recordPregChecks, isPending } =
    useRecordOnFarmImplantPregChecks();

  const resultItems = useMemo(
    () =>
      RESULT_OPTIONS.map((option) => ({
        value: String(option.value),
        label: t(`onFarmImplant.pregCheck.results.${option.key}`),
      })),
    [t],
  );

  const checkedByValue = checkedById ?? defaultCheckedById;
  const missingResult = result === '';
  const missingDate = !checkDate;
  const missingCheckedBy = checkedByValue === '';
  const canSave =
    rows.length > 0 &&
    !missingResult &&
    !missingDate &&
    !missingCheckedBy &&
    !isPending;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setResult('');
        setCheckDate(new Date());
        setCheckedById(null);
        setProblems([]);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleSave = useCallback(async () => {
    if (!canSave || !checkDate) return;

    setProblems([]);
    try {
      const saved = await recordPregChecks({
        sessionId,
        input: {
          sessionQueueIds: rows.map((row) => row.sessionQueueId),
          result: Number(result),
          checkDate: toWallClockDate(checkDate),
          checkedById: checkedByValue,
          versions: collectVersions(rows),
        },
      });

      onSaved();
      handleOpenChange(false);
      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.pregCheck.update.success', {
          count: saved.length,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }
      console.error('Failed to save preg check results:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.pregCheck.update.failed'),
      });
    }
  }, [
    canSave,
    checkDate,
    checkedByValue,
    handleOpenChange,
    onSaved,
    recordPregChecks,
    result,
    rows,
    sessionId,
    t,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('onFarmImplant.pregCheck.update.title')}</DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.pregCheck.update.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-[8rem_1fr] items-center gap-3">
            <Label className="text-xs" htmlFor="preg-check-result">
              {t('onFarmImplant.pregCheck.update.result')}
            </Label>
            <Select
              items={resultItems}
              value={result || null}
              onChange={(value) => setResult((value as string | null) ?? '')}
            >
              <SelectTrigger
                id="preg-check-result"
                className="h-8 w-full text-xs"
              >
                <SelectValue
                  placeholder={t('onFarmImplant.pregCheck.update.selectResult')}
                />
              </SelectTrigger>
              <SelectContent>
                {resultItems.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Label className="text-xs" htmlFor="preg-check-date">
              {t('onFarmImplant.pregCheck.update.checkDate')}
            </Label>
            <DatePicker
              id="preg-check-date"
              value={checkDate}
              onChange={setCheckDate}
              placeholder={t('onFarmImplant.pregCheck.update.selectDate')}
              className="h-8 w-full text-xs"
            />

            <Label className="text-xs" htmlFor="preg-check-checked-by">
              {t('onFarmImplant.pregCheck.update.checkedBy')}
            </Label>
            <SearchableSelect
              className="h-8 w-full text-xs"
              options={techOptions}
              value={checkedByValue}
              onChange={(value) => setCheckedById(value)}
              placeholder={t('onFarmImplant.pregCheck.update.selectCheckedBy')}
              emptyMessage={t(
                'onFarmImplant.implantQueue.massChange.noOptions',
              )}
            />
          </div>

          {(missingDate || missingCheckedBy) && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.pregCheck.update.required')}
            </p>
          )}

          <p className="text-xs text-gray-500">
            {t('onFarmImplant.pregCheck.update.sameDateHint')}
          </p>

          <p className="text-sm font-medium text-gray-700">
            {t('onFarmImplant.pregCheck.update.recordCount', {
              count: rows.length,
            })}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.pregCheck.update.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
